import { EditablePreviewWorld, Vec3 } from '../types'
import { FRAME_SECONDS } from './constants'
import { BOSS_BGM_URL, CLEAR_BGM_URL } from './missionBgm'
import {
  TemplateCloneRestorer,
  createTemplateCloneRestorer,
} from './primitives/restoreClonedTemplates'
import { createWarpPadHandler } from './primitives/warpPad'
import {
  findIdByName,
  findStateByName,
  nextTempId,
  showHudText,
  spawnProgrammedClone,
} from './sharedHelpers'
import {
  BehaviorContext,
  BehaviorHandler,
  HudStyle,
  MissionBehaviors,
} from './types'

/**
 * RJS12 のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS12.rbxl）のギミックを移植する:
 * - ServerScriptService/StageScript: プレイ開始時に 11×15 マスの迷路を
 *   再帰バックトラック法で自動生成し、壁マスへ ServerStorage の ⑥ブロック の
 *   クローンを配置する（入口マス z=1, x=2 は空ける）。さらに迷路の奥側
 *   （z=11..14, x=2..10）の通路マスへ ServerStorage の Goal（あおいブロック）を
 *   1 個配置する。
 * - ServerStorage/Goal/GoalScript: Goal にキャラクターが触れると
 *   workspace.Warp の位置へテレポートする。
 * - Workspace/BabyNessie/NessieScript: 毎フレーム、②ボスのかべ が存在する間は
 *   ボス（Torso）を かべの位置 + (0,0,50) に追従させ、かべが消えたら
 *   ボスのモデル（Head + Torso）を消す。
 * - Workspace/Wall2/WallScript: BindableEvent「クリア」で出口側の壁
 *   （Wall2）を消す。
 * - ServerScriptService/Manager + StarterGui/LocalScript: GUI の文言を
 *   「こんどこそ まけないぞ！」（初期。実際の文言は全角空白）→
 *   「にがさないぞ！」（スタート）→
 *   「まけた・・・すごいね！」（クリア）/「ぼくのかちだ！」（ゲームオーバー）
 *   と切り替える。プレビューでは学習者コードの「メッセージを送る」が
 *   同名メッセージ（スタート/クリア/ゲームオーバー）として届く。
 *
 * プレビューでの近似・意図的な差異:
 * - GoalScript の Lua は HumanoidRootPart.Position へ Warp の位置を直接代入する。
 *   Roblox の Position セッターは衝突解決（上方への押し出し）を行い、結果として
 *   キャラクターは Warp 地点の床上に立つ。プレビューでは同じ結果になる
 *   ワープパッド（到着先 + 上方 5 studs へテレポートし接地）で再現する。
 * - ServerStorage のテンプレート（⑤ボスのこうげき / ⑥ブロック）はシーンに
 *   代役として置かれているので、実機同様プレイ中は隠す。
 * - Manager の BGM 切替（Cat Chase → Pirate Battle → 8 Bit Kicks (a) (60)）は、
 *   プロダクト提供の 3 曲（Sunrise → Thunderbolt → ユメミライ）で再現する。
 *   初期 BGM はシーン記述（SceneDescriptor.bgm）、スタート・クリアでの切替は
 *   BehaviorContext.setBgm による差し替えで鳴らす（クリアは実機同様、表示・
 *   壁消去のあと 3 秒待ってから切り替える）。
 *
 * 未対応:
 * - Lamp/LightScript（街灯の点灯）: 受信側の CustomEventReceiver「Luminate」に
 *   対応する発火源（CustomEvent）が rbxl 内に存在せず、実機でも一度も発火
 *   しない死にコードのため移植しない。
 * - Sports Car（endorsed vehicle のスクリプト群）: 車両の挙動はプレビューの
 *   車両システム（SceneDescriptor.vehicles）の領域であり、環境ビヘイビアでは
 *   扱わない（RJS12 のシーン JSON では車両未検出）。
 */

/** 迷路の横マス数（Lua: stageSizeX）。 */
const STAGE_SIZE_X = 11

/** 迷路の縦マス数（Lua: stageSizeZ）。 */
const STAGE_SIZE_Z = 15

/**
 * 迷路掘削の方向候補（[dz, dx]。Lua: direction = {{1,0},{-1,0},{0,1},{0,-1}}）。
 */
const MAZE_DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
]

/** 迷路掘削の開始マス（Lua: initialize の stage[2][2] = 0 と createMaze(2,2)）。 */
const MAZE_START_Z = 2
const MAZE_START_X = 2

/** 入口マス（Lua: createBlock が z==1 and x==2 だけブロックを置かない）。 */
const ENTRANCE_Z = 1
const ENTRANCE_X = 2

/** Goal を置くマスの抽選範囲（Lua: rand:NextInteger(11, stageSizeZ-1) 等）。 */
const GOAL_Z_MIN = 11
const GOAL_Z_MAX = STAGE_SIZE_Z - 1
const GOAL_X_MIN = 2
const GOAL_X_MAX = STAGE_SIZE_X - 1

/** ServerStorage のテンプレートのオブジェクト名。 */
const BLOCK_TEMPLATE_NAME = '⑥ブロック'
const BOSS_ATTACK_TEMPLATE_NAME = '⑤ボスのこうげき'

/** Goal（あおいブロック）の rbxl 実測値。シーンに無いため定数から生成する。 */
const GOAL_NAME = 'Goal'
const GOAL_SIZE: Vec3 = { x: 10, y: 10, z: 10 }
const GOAL_COLOR = '#04AFEC'

/** ボス追従関連のオブジェクト名（BabyNessie の部位と追従先）。 */
const BOSS_WALL_NAME = '②ボスのかべ'
const NESSIE_TORSO_NAME = 'Torso'
const NESSIE_HEAD_NAME = 'Head'

/** ボスの追従オフセット（Lua: wall.Position + Vector3.new(0,0,50)）。 */
const NESSIE_FOLLOW_OFFSET_Z = 50

/** クリアで消える出口側の壁と、Goal 接触のテレポート先のオブジェクト名。 */
const CLEAR_WALL_NAME = 'Wall2'
const WARP_NAME = 'Warp'

/** クリア後、BGM をクリア曲へ切り替えるまでの秒数（Lua: Manager.clear() の wait(3)）。 */
const CLEAR_BGM_DELAY_SECONDS = 3

/** GUI の文言（実機 GUIText / LocalScript の実測値）。 */
const INITIAL_TEXT = 'こんどこそ　まけないぞ！'
const START_TEXT = 'にがさないぞ！'
const CLEAR_TEXT = 'まけた・・・すごいね！'
const GAMEOVER_TEXT = 'ぼくのかちだ！'

/** ゲーム GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const GUI_HUD_STYLE: HudStyle = {
  background: '#005500',
  color: '#FFFF7F',
  border: '#000000',
  borderWidth: 5,
  wide: true,
}

/** Lua の rand:NextInteger(min, max)（両端を含む一様整数）。 */
const randomIntInclusive = (min: number, max: number): number =>
  min + Math.floor(Math.random() * (max - min + 1))

/**
 * マス座標 → ワールド座標（Lua: Vector3.new(x * 10 - 60, 5, z * -10 + 100)）。
 */
const cellToWorld = (z: number, x: number): Vec3 => ({
  x: x * 10 - 60,
  y: 5,
  z: z * -10 + 100,
})

/**
 * 迷路グリッドを生成する（Lua: initialize + createMaze）。
 * 値は Lua と同じく 1 = ブロック、0 = 通路。Lua の 1 始まり添字を保つため
 * 添字 0 の行・列は使わない（常に 1 のまま）。
 */
const buildMazeStage = (): number[][] => {
  const stage: number[][] = Array.from({ length: STAGE_SIZE_Z + 1 }, () =>
    Array.from({ length: STAGE_SIZE_X + 1 }, () => 1),
  )
  stage[MAZE_START_Z][MAZE_START_X] = 0
  carveMaze(stage, MAZE_START_Z, MAZE_START_X)
  return stage
}

/**
 * 再帰バックトラック法で迷路を掘る（Lua: createMaze）。
 * ランダムな方向から順に 4 方向を試し、2 マス先が未掘削なら間のマスごと
 * 掘って再帰する。
 */
const carveMaze = (stage: number[][], z: number, x: number): void => {
  // Lua: local rand = Random.new(); local number = rand:NextInteger(1,4)
  let directionIndex = randomIntInclusive(1, 4)
  for (let i = 1; i <= 4; i += 1) {
    const [dz, dx] = MAZE_DIRECTIONS[directionIndex - 1]
    const nextZ = z + dz * 2
    const nextX = x + dx * 2
    if (
      nextZ >= 1 &&
      nextZ <= STAGE_SIZE_Z &&
      nextX >= 1 &&
      nextX <= STAGE_SIZE_X &&
      stage[nextZ][nextX] === 1
    ) {
      // 2 マス先と、間の通路マスを掘って先へ進む。
      stage[nextZ][nextX] = 0
      stage[z + dz][x + dx] = 0
      carveMaze(stage, nextZ, nextX)
    }
    directionIndex = directionIndex === 4 ? 1 : directionIndex + 1
  }
}

/**
 * Goal を置くマスを抽選する（Lua: moveGoal）。迷路の奥側の範囲から、
 * 通路マス（stage == 0）を引くまで引き直す。
 */
const pickGoalCell = (stage: number[][]): { z: number; x: number } => {
  let z = randomIntInclusive(GOAL_Z_MIN, GOAL_Z_MAX)
  let x = randomIntInclusive(GOAL_X_MIN, GOAL_X_MAX)
  while (stage[z][x] === 1) {
    z = randomIntInclusive(GOAL_Z_MIN, GOAL_Z_MAX)
    x = randomIntInclusive(GOAL_X_MIN, GOAL_X_MAX)
  }
  return { z, x }
}

/**
 * Goal（あおいブロック）を 1 個生成する。実機の複製元は ServerStorage の
 * Goal パーツだが、シーン JSON には含まれないため rbxl の実測値から作る。
 * Goal に学習者プログラムは無い（環境の onTouchCharacter でワープさせる）。
 */
const spawnGoal = (
  world: EditablePreviewWorld,
  cell: { z: number; x: number },
): void => {
  const id = nextTempId(world, 'rjs12_goal')
  world.addObject({
    id,
    name: GOAL_NAME,
    position: cellToWorld(cell.z, cell.x),
    size: { ...GOAL_SIZE },
    color: GOAL_COLOR,
    material: 'WoodPlanks',
    isStatic: true,
    canCollide: true,
    canTouch: true,
  })
}

/**
 * StageScript の移植。迷路の壁ブロックと Goal をプレイ開始時に配置する。
 * Roblox の Clone() は配下の Script ごと複製するため、⑥ブロック のクローンには
 * 学習者がテンプレートに付けたプログラムを起動する。
 */
const runStageScript = (
  ctx: BehaviorContext,
  templateRestorer: TemplateCloneRestorer,
): void => {
  const { world } = ctx
  // 実機の ServerStorage 同様、テンプレート本体はプレイ中隠す。クローンの
  // 複製元は隠蔽前のスナップショット（実機の ServerStorage に保存された状態に
  // 相当。学習者の control_clone 複製への状態復元も同じスナップショットで
  // 行う。詳細は primitives/restoreClonedTemplates.ts）。
  const snapshotByName = templateRestorer.concealAndSnapshot(world)
  const blockSnapshot = snapshotByName.get(BLOCK_TEMPLATE_NAME)
  // テンプレートが欠けたシーン（ハードコードシーン等）では配置しない。
  if (!blockSnapshot) {
    return
  }
  const stage = buildMazeStage()
  for (let z = 1; z <= STAGE_SIZE_Z; z += 1) {
    for (let x = 1; x <= STAGE_SIZE_X; x += 1) {
      if (stage[z][x] !== 1) {
        continue
      }
      // Lua: 入口マス（z=1, x=2）だけはブロックを置かない。
      if (z === ENTRANCE_Z && x === ENTRANCE_X) {
        continue
      }
      spawnProgrammedClone(ctx, blockSnapshot, 'rjs12_block', cellToWorld(z, x))
    }
  }
  spawnGoal(world, pickGoalCell(stage))
}

/**
 * NessieScript の移植。②ボスのかべ が存在する間はボス（Torso）を かべの
 * 位置 + (0,0,50) に毎フレーム追従させ、かべが消えたらボスのモデル
 * （Head + Torso）を消す。Lua は PrimaryPart（Torso）の Position のみ動かし、
 * Head は Anchored の透明パーツとしてその場に残るが、Destroy はモデルごと
 * 消すため両方を対象にする。
 */
const runNessieScript = async (ctx: BehaviorContext): Promise<void> => {
  const { world } = ctx
  while (!ctx.signal.aborted) {
    const torso = findStateByName(world, NESSIE_TORSO_NAME)
    if (!torso) {
      return
    }
    const wall = findStateByName(world, BOSS_WALL_NAME)
    if (wall) {
      // Lua: primaryPart.Position = wall.Position + Vector3.new(0,0,50)
      world.setPosition(torso.id, {
        x: wall.position.x,
        y: wall.position.y,
        z: wall.position.z + NESSIE_FOLLOW_OFFSET_Z,
      })
    } else {
      // Lua: script.Parent:Destroy()（BabyNessie モデルごと消える）。
      const head = findStateByName(world, NESSIE_HEAD_NAME)
      if (head) {
        world.destroy(head.id)
      }
      world.destroy(torso.id)
      return
    }
    await ctx.wait(FRAME_SECONDS)
  }
}

export const createRjs12Behaviors = (): MissionBehaviors => {
  // テンプレートの隠蔽 + 学習者の control_clone 複製への状態復元。
  const templateRestorer = createTemplateCloneRestorer([
    BLOCK_TEMPLATE_NAME,
    BOSS_ATTACK_TEMPLATE_NAME,
  ])

  // 実機の TextLabel は初期表示（Visible=true・GUIText の初期値）。
  const showInitialGuiText: BehaviorHandler = showHudText(
    INITIAL_TEXT,
    GUI_HUD_STYLE,
  )

  const onMessage = new Map<string, BehaviorHandler>()

  // Lua: storage["スタート"].Event → StartRemoteEvent → 「にがさないぞ！」。
  // 同時に BGM をボス戦曲（Thunderbolt）へ差し替える。
  onMessage.set('スタート', (ctx) => {
    ctx.setBgm?.(BOSS_BGM_URL)
    ctx.setHudText?.(START_TEXT, GUI_HUD_STYLE)
  })

  // Lua: storage["クリア"].Event → ClearRemoteEvent →「まけた・・・すごいね！」
  // + WallScript が出口側の壁（Wall2）を消す。その後 wait(3) して BGM を
  // クリア曲（ユメミライ）へ切り替える。
  onMessage.set('クリア', async (ctx) => {
    ctx.setHudText?.(CLEAR_TEXT, GUI_HUD_STYLE)
    const wallId = findIdByName(ctx.world, CLEAR_WALL_NAME)
    if (wallId) {
      ctx.world.destroy(wallId)
    }
    await ctx.wait(CLEAR_BGM_DELAY_SECONDS)
    // 停止（abort）時はランタイム側が BGM を止めるため何もしない。
    if (!ctx.signal.aborted) {
      ctx.setBgm?.(CLEAR_BGM_URL)
    }
  })

  // Lua: storage["ゲームオーバー"].Event →「ぼくのかちだ！」。
  onMessage.set('ゲームオーバー', (ctx) => {
    ctx.setHudText?.(GAMEOVER_TEXT, GUI_HUD_STYLE)
  })

  // Lua: GoalScript — Goal に触れたら workspace.Warp の位置へテレポート。
  const onTouchCharacter = new Map<string, BehaviorHandler>()
  onTouchCharacter.set(GOAL_NAME, createWarpPadHandler(WARP_NAME))

  return {
    // watchClones は runStageScript（隠蔽）と closure 経由でスナップショットを
    // 共有するため末尾でよい（既存テストの onPlay[0..2] 参照も維持される）。
    onPlay: [
      showInitialGuiText,
      (ctx) => runStageScript(ctx, templateRestorer),
      runNessieScript,
      templateRestorer.watchClones,
    ],
    onTouchCharacter,
    onMessage,
    // ServerStorage のテンプレート本体では学習者プログラムを起動しない
    // （⑥ブロック のクローンには startObjectPrograms 経由で起動する）。
    serverStorageTemplates: [BOSS_ATTACK_TEMPLATE_NAME, BLOCK_TEMPLATE_NAME],
  }
}
